import { sideBarOptions } from "@/src/constants/sidebarConstants.js";
import { standardObjects } from "@/src/constants/objectConstants.js";
import { permissionConstants } from "@/src/constants/permissionConstants.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";

export const CHATS_ROUTE = "/projects/chats";

export const isChatsSection = (section) => {
  if (!section) return false;
  if (section.route === CHATS_ROUTE) return true;
  const id = (section.id || "").toLowerCase();
  return id === "chats" || id === "chat";
};

export const generateDynamicImageName = (name, type = "SELECTED") => {
  const normalizedName = name.toUpperCase().replace(/[^A-Z0-9]/g, "_");
  return `${normalizedName}_${type}`;
};

export const createDynamicCRMSections = (
  dynamicSections,
  appId = null,
  appName = null
) => {
  return dynamicSections.map((section) => {
    // crm_tasks should route to /tasks instead of /crm/crm_tasks
    const isCrmTasks = section.slug === entityTypes.CRM_TASKS;
    // Append ?service= so same-slug objects across services (e.g. CRM
    // products and the products-service products) point to distinct pages.
    const serviceQuery =
      !isCrmTasks && section.service ? `?service=${section.service}` : "";

    return {
      id: section.service
        ? `${section.slug}::${section.service}`
        : section.slug,
      text: section.plural_name || section.name,
      permission: permissionConstants.ALL,
      selected: section.icon
        ? imageNames[generateDynamicImageName(section.icon, "SELECTED")]
        : imageNames.CUSTOMERS_SELECTED,
      unselected: section.icon
        ? imageNames[generateDynamicImageName(section.icon, "UNSELECTED")]
        : imageNames.CUSTOMERS_UNSELECTED,
      route: isCrmTasks ? "/tasks" : `/crm/${section.slug}${serviceQuery}`,
      appId: appId,
      appName: appName,
      sub_sections: section.sub_sections || [],
      service: section.service || null,
      slug: section.slug,
    };
  });
};

export const getSideBarOptionsWithDynamicCRM = (
  dynamicSections = [],
  baseOptions = null,
  appId = null,
  appName = null
) => {
  const dynamicCRMSections = createDynamicCRMSections(
    dynamicSections,
    appId,
    appName
  );
  const optionsToUse = baseOptions || sideBarOptions;

  const standardSlugSet = new Set(standardObjects.map((obj) => obj.slug));
  const standardOrderMap = Object.fromEntries(
    standardObjects.map((obj, index) => [obj.slug, index])
  );

  // Dynamic CRM sections carry `slug` alongside a service-namespaced `id`
  // (e.g. id="contacts::crm", slug="contacts"), so resolve via slug first.
  const getSectionSlug = (section) => section.slug || section.id;
  const isStandard = (section) => standardSlugSet.has(getSectionSlug(section));
  const sortByStandardOrder = (sections) =>
    sections.sort(
      (a, b) =>
        (standardOrderMap[getSectionSlug(a)] ?? 9999) -
        (standardOrderMap[getSectionSlug(b)] ?? 9999)
    );

  return optionsToUse.map((option) => {
    const normalizedId = option.id?.toLowerCase().replace(/[^a-z0-9]/g, "");
    if (normalizedId !== "crm") return option;

    const baseSections = option.sections || [];
    const standardBase = baseSections.filter(isStandard);
    const otherBase = baseSections.filter((s) => !isStandard(s));
    const standardDynamic = dynamicCRMSections.filter(isStandard);
    const customDynamic = dynamicCRMSections.filter((s) => !isStandard(s));

    return {
      ...option,
      sections: [
        ...sortByStandardOrder(standardBase),
        ...sortByStandardOrder(standardDynamic),
        ...otherBase,
        ...customDynamic,
      ],
    };
  });
};
